import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Package, Search, ChevronDown, ChevronRight, HelpCircle, ArrowRight, MapPin, Truck, Check, Clock, Box, CircleDot } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useRegion } from '../../contexts/RegionContext';
import { supabase } from '../../supabaseClient';

const STATUS_STEPS = [
  { key: 'confirmed', label: 'Order Confirmed', icon: Check },
  { key: 'processing', label: 'Processing', icon: Clock },
  { key: 'shipped', label: 'Shipped', icon: Truck },
  { key: 'out_for_delivery', label: 'Out for Delivery', icon: Box },
  { key: 'delivered', label: 'Delivered', icon: Package },
];

const OrdersPage = () => {
  const { user } = useAuth();
  const { formatPrice } = useRegion();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [orderNumber, setOrderNumber] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [showFaq, setShowFaq] = useState(false);

  const maskedEmail = user?.email
    ? user.email.replace(/(.{2})(.*)(@.*)/, (_, a, b, c) => a + '•'.repeat(b.length) + c)
    : '';

  // Fetch user's orders from Supabase
  useEffect(() => {
    const fetchOrders = async () => {
      if (!user?.id) return;
      try {
        const { data, error } = await supabase
          .from('store_orders')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (!error && data) {
          setOrders(data);
        }
      } catch (err) {
        console.error('Error fetching orders:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, [user?.id]);

  const getStatusIndex = (status) => {
    const idx = STATUS_STEPS.findIndex(s => s.key === status);
    return idx >= 0 ? idx : 0;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'text-green-600';
      case 'shipped': case 'out_for_delivery': return 'text-[#065DC0]';
      case 'cancelled': case 'returned': return 'text-red-500';
      default: return 'text-[#6e6e73]';
    }
  };

  const getStatusLabel = (status) => {
    const step = STATUS_STEPS.find(s => s.key === status);
    if (step) return step.label;
    if (status === 'cancelled') return 'Cancelled';
    if (status === 'returned') return 'Returned';
    return status;
  };

  // Order Card
  const OrderCard = ({ order }) => {
    const isExpanded = expandedOrder === order.id;
    const statusIdx = getStatusIndex(order.status);
    const isCancelled = order.status === 'cancelled' || order.status === 'returned';

    return (
      <motion.div
        layout
        className="border border-[#d2d2d7] dark:border-white/[0.08] rounded-[32px] overflow-hidden bg-white dark:bg-[#1c1c1e]"
      >
        {/* Order Header */}
        <button
          onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
          className="w-full text-left p-6 bg-transparent border-none cursor-pointer"
        >
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <p className="text-[10px] font-bold text-[#86868b] uppercase tracking-[0.2em] mb-1">Order</p>
              <p className="text-[15px] font-bold text-[#1d1d1f] dark:text-[#f5f5f7]">{order.order_number}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-bold text-[#86868b] uppercase tracking-[0.2em] mb-1">Placed</p>
              <p className="text-sm text-[#1d1d1f] dark:text-[#f5f5f7]">{formatDate(order.created_at)}</p>
            </div>
          </div>

          {/* Items Preview */}
          <div className="flex gap-3 mb-4 overflow-x-auto pb-1">
            {order.items?.map((item, i) => (
              <div key={i} className="shrink-0 w-16 h-16 rounded-[32px] bg-[#f5f5f7] dark:bg-white/[0.05] flex items-center justify-center">
                <img src={item.image} alt={item.name} className="w-full h-full object-contain" />
              </div>
            ))}
          </div>

          {/* Status + Total Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CircleDot size={14} className={getStatusColor(order.status)} />
              <span className={`text-sm font-semibold ${getStatusColor(order.status)}`}>
                {getStatusLabel(order.status)}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-bold text-[#1d1d1f] dark:text-[#f5f5f7]">{formatPrice(order.total)}</span>
              <ChevronDown size={18} className={`text-[#86868b] transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
            </div>
          </div>
        </button>

        {/* Expanded Details */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="px-6 pb-6 border-t border-[#d2d2d7] dark:border-white/[0.08] pt-5 space-y-6">

                {/* Tracking Progress Bar */}
                {!isCancelled && (
                  <div>
                    <h4 className="text-xs font-bold text-[#86868b] uppercase tracking-[0.2em] mb-4">Tracking</h4>
                    <div className="flex items-center gap-0 overflow-x-auto pb-4 scrollbar-hide">
                      {STATUS_STEPS.map((step, i) => {
                        const isCompleted = i <= statusIdx;
                        const isCurrent = i === statusIdx;
                        const StepIcon = step.icon;
                        return (
                          <React.Fragment key={step.key}>
                            <div className="flex flex-col items-center gap-1.5 relative shrink-0">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                                isCompleted ? 'bg-[#065DC0] text-white' : 'bg-[#f5f5f7] dark:bg-white/[0.05] text-[#86868b]'
                              } ${isCurrent ? 'ring-2 ring-[#065DC0]/30 ring-offset-2 dark:ring-offset-[#1c1c1e]' : ''}`}>
                                <StepIcon size={14} />
                              </div>
                              <span className={`text-[9px] font-semibold text-center leading-tight min-w-[64px] ${
                                isCompleted ? 'text-[#065DC0]' : 'text-[#86868b]'
                              }`}>
                                {step.label}
                              </span>
                            </div>
                            {i < STATUS_STEPS.length - 1 && (
                              <div className={`flex-1 h-[2px] min-w-[30px] mx-1 rounded-full shrink-0 ${
                                i < statusIdx ? 'bg-[#065DC0]' : 'bg-[#d2d2d7] dark:bg-white/[0.08]'
                              }`} />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>

                    {/* Tracking Number */}
                    {order.tracking_number && (
                      <div className="mt-4 p-3 bg-[#f5f5f7] dark:bg-white/[0.03] rounded-[32px]">
                        <p className="text-xs text-[#86868b] mb-0.5">Tracking Number</p>
                        <p className="text-sm font-medium text-[#1d1d1f] dark:text-[#f5f5f7]">
                          {order.tracking_number}
                          {order.tracking_carrier && <span className="text-[#86868b]"> ({order.tracking_carrier})</span>}
                        </p>
                        {order.tracking_url && (
                          <a href={order.tracking_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#065DC0] hover:underline mt-1 inline-flex items-center gap-1">
                            Track Package <ArrowRight size={10} />
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Items */}
                <div>
                  <h4 className="text-xs font-bold text-[#86868b] uppercase tracking-[0.2em] mb-3">Items</h4>
                  <div className="space-y-3">
                    {order.items?.map((item, i) => (
                      <div key={i} className="flex gap-4">
                        <div className="w-16 h-16 rounded-[32px] bg-[#f5f5f7] dark:bg-white/[0.05] flex items-center justify-center shrink-0">
                          <img src={item.image} alt={item.name} className="w-full h-full object-contain" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-[#1d1d1f] dark:text-[#f5f5f7]">{item.name}</p>
                          {item.config?.map(({ label, value }) => (
                            <p key={label} className="text-xs text-[#86868b]">{value}</p>
                          ))}
                          <p className="text-xs text-[#86868b]">Qty: {item.quantity}</p>
                        </div>
                        <p className="text-sm font-semibold text-[#1d1d1f] dark:text-[#f5f5f7] shrink-0">{formatPrice(item.price * item.quantity)}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Order Summary */}
                <div>
                  <h4 className="text-xs font-bold text-[#86868b] uppercase tracking-[0.2em] mb-3">Summary</h4>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#86868b]">Subtotal</span>
                      <span className="text-[#1d1d1f] dark:text-[#f5f5f7] font-medium">{formatPrice(order.subtotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#86868b]">Shipping</span>
                      <span className="text-[#1d1d1f] dark:text-[#f5f5f7] font-medium">{order.shipping_cost === 0 ? 'FREE' : formatPrice(order.shipping_cost)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-[#d2d2d7] dark:border-white/[0.08]">
                      <span className="font-bold text-[#1d1d1f] dark:text-[#f5f5f7]">Total</span>
                      <span className="font-bold text-[#1d1d1f] dark:text-[#f5f5f7]">{formatPrice(order.total)}</span>
                    </div>
                  </div>
                </div>

                {/* Shipping & Payment */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-4 bg-[#f5f5f7] dark:bg-white/[0.03] rounded-[32px]">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin size={14} className="text-[#065DC0]" />
                      <p className="text-xs font-bold text-[#86868b] uppercase tracking-[0.15em]">Ships to</p>
                    </div>
                    <p className="text-sm font-medium text-[#1d1d1f] dark:text-[#f5f5f7]">
                      {order.shipping_address?.firstName} {order.shipping_address?.lastName}
                    </p>
                    <p className="text-xs text-[#86868b]">{order.shipping_address?.address}</p>
                    <p className="text-xs text-[#86868b]">{order.shipping_address?.city}, {order.shipping_address?.state} {order.shipping_address?.zip}</p>
                    <p className="text-xs text-[#86868b]">{order.shipping_address?.country}</p>
                  </div>

                  <div className="p-4 bg-[#f5f5f7] dark:bg-white/[0.03] rounded-[32px]">
                    <div className="flex items-center gap-2 mb-2">
                      <Truck size={14} className="text-[#065DC0]" />
                      <p className="text-xs font-bold text-[#86868b] uppercase tracking-[0.15em]">Delivery</p>
                    </div>
                    <p className="text-sm font-medium text-[#1d1d1f] dark:text-[#f5f5f7]">
                      {order.delivery_method === 'express' ? 'Express' : 'Standard'}
                    </p>
                    {order.estimated_delivery && (
                      <p className="text-xs text-[#86868b]">Est. {formatDate(order.estimated_delivery)}</p>
                    )}
                    <p className="text-xs text-[#86868b] mt-1">
                      Payment: {order.payment_method === 'fozzil-pay' ? 'FOZZIL Pay' : order.payment_method === 'card' ? 'Card' : order.payment_method === 'upi' ? 'UPI' : 'Net Banking'}
                    </p>
                  </div>
                </div>

                {/* Status History */}
                {order.status_history?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold text-[#86868b] uppercase tracking-[0.2em] mb-3">Activity</h4>
                    <div className="space-y-2">
                      {order.status_history.map((entry, i) => (
                        <div key={i} className="flex gap-3 items-start">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#065DC0] mt-1.5 shrink-0" />
                          <div>
                            <p className="text-sm text-[#1d1d1f] dark:text-[#f5f5f7] font-medium">{entry.note || getStatusLabel(entry.status)}</p>
                            <p className="text-xs text-[#86868b]">{formatDateTime(entry.timestamp)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <a href="https://support.fozzil.in/contact" className="px-5 py-2.5 rounded-full border border-[#d2d2d7] dark:border-white/[0.15] text-sm font-semibold text-[#1d1d1f] dark:text-[#f5f5f7] hover:bg-[#f5f5f7] dark:hover:bg-white/[0.05] transition-all text-center">
                    Get Help
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  return (
    <div className="store-page bg-white dark:bg-black text-[#1d1d1f] dark:text-[#f5f5f7] min-h-screen transition-colors duration-400">
      {/* Sub-header */}
      <div className="border-b border-[#d2d2d7] dark:border-white/[0.08]">
        <div className="max-w-[980px] mx-auto px-6 py-4">
          <h2 className="text-sm font-bold text-[#1d1d1f] dark:text-[#f5f5f7]">Your Orders</h2>
        </div>
      </div>

      <div className="max-w-[980px] mx-auto px-6">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="py-16"
        >
          <h1 className="text-[40px] md:text-[56px] font-bold tracking-tight leading-tight">
            Products you've ordered.
          </h1>
        </motion.div>

        {/* Loading State */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-[#d2d2d7] border-t-[#065DC0] rounded-full animate-spin" />
          </div>
        ) : orders.length > 0 ? (
          /* Orders List */
          <div className="space-y-4 pb-16">
            {orders.map((order) => (
              <OrderCard key={order.id} order={order} />
            ))}
          </div>
        ) : (
          /* Empty State */
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="pb-16"
          >
            <p className="text-[15px] text-[#6e6e73] mb-8">
              Only purchases from the last 18 months will be shown here.
            </p>

            <div className="mb-10">
              <h2 className="text-[21px] font-bold text-[#1d1d1f] dark:text-[#f5f5f7] mb-4">Order Help</h2>
              <p className="text-[15px] text-[#6e6e73] mb-1">
                Can't see your order? <Link to="/shop" className="text-[#065DC0] hover:underline inline-flex items-center gap-1">Find it now <ArrowRight size={12} /></Link>
              </p>
            </div>

            <div className="mb-10">
              <p className="text-[15px] text-[#6e6e73] mb-2">
                You're currently signed in as <strong className="text-[#1d1d1f] dark:text-[#f5f5f7]">{maskedEmail}</strong>
              </p>
              <p className="text-[15px] text-[#6e6e73]">
                Some users have more than one FOZZIL Account. This might apply to you. If you have another account, please sign out, then sign in again with a different email address.
              </p>
            </div>

            <div className="mb-10">
              <p className="text-[15px] text-[#6e6e73]">
                Need more help? <a href="https://support.fozzil.in/contact" className="text-[#065DC0] hover:underline">Contact FOZZIL Support</a>
              </p>
            </div>
          </motion.div>
        )}

        {/* Divider */}
        <div className="border-t border-[#d2d2d7] dark:border-white/[0.08]" />

        {/* Find Your Order Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="py-16 grid grid-cols-1 md:grid-cols-2 gap-16"
        >
          <div>
            <h2 className="text-[28px] font-bold text-[#1d1d1f] dark:text-[#f5f5f7] mb-2">Find your order.</h2>
          </div>
          <div>
            <h3 className="text-[17px] font-bold text-[#1d1d1f] dark:text-[#f5f5f7] mb-1">Look it up with your order number.</h3>
            <p className="text-[15px] text-[#6e6e73] mb-6">Find an individual order.</p>

            <div className="space-y-4">
              <div>
                <input
                  type="text"
                  placeholder="Order number"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  className="w-full px-4 py-3.5 rounded-[32px] border border-[#d2d2d7] dark:border-white/[0.15] text-[15px] text-[#1d1d1f] dark:text-[#f5f5f7] bg-white dark:bg-[#1c1c1e] outline-none focus:border-[#065DC0] transition-colors placeholder-[#86868b]"
                />
              </div>
              <div>
                <input
                  type="email"
                  placeholder="Email Address"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  className="w-full px-4 py-3.5 rounded-[32px] border border-[#d2d2d7] dark:border-white/[0.15] text-[15px] text-[#1d1d1f] dark:text-[#f5f5f7] bg-white dark:bg-[#1c1c1e] outline-none focus:border-[#065DC0] transition-colors placeholder-[#86868b]"
                />
              </div>
              <button className="w-fit px-8 py-3.5 mx-auto rounded-full bg-[#065DC0] text-white text-[15px] font-semibold border-none cursor-pointer hover:bg-[#0077ed] transition-all">
                Continue
              </button>
              <p className="text-[13px] text-[#065DC0] flex items-center gap-1.5 cursor-pointer hover:underline">
                <HelpCircle size={14} /> How to find your order number
              </p>
            </div>
          </div>
        </motion.div>

        {/* Divider */}
        <div className="border-t border-[#d2d2d7] dark:border-white/[0.08]" />
    
        {/* Encryption notice */}
        <div className="border-t border-[#d2d2d7] dark:border-white/[0.08] py-6 mb-8">
          <p className="text-xs text-[#86868b] text-center">
            The FOZZIL Online Store uses industry-standard encryption to protect the confidentiality of the information you submit.
            Learn more about our <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#065DC0] hover:underline">Security Policy</Link>.
          </p>
        </div>        
      </div>
    </div>
  );
};

export default OrdersPage;
